// locationSeen.js — when did this reader last open this location?
// ---------------------------------------------------------------------------
// v0.242.0 began it; v0.338.0 (2026-11-04) moved the memory to the server.
//
// The rule behind the "new report" marker on the Locations list is unchanged
// and still made of two dates: a location is new to you when its newest report
// is newer than the last time YOU opened it. The marker clears by being acted
// on (the click that opens the location), never by the list drawing itself.
//
// WHY THE SERVER NOW. Until v0.338.0 the "last opened" half lived in this
// browser alone. Brett, on screenshots of a client's home: "They shouldn't show
// as new report. He has seen them all." A client on a new phone or laptop saw
// every site as new. The mark is now kept per PERSON (/api/location-seen:
// office sign-ins by user, clients and trades on their link by contact), so it
// follows them from device to device.
//
// THIS BROWSER STILL KEEPS A COPY, per person (KEY_PREFIX + identity), so the
// list draws at once and still works offline or while the database wakes. The
// copy and the server are merged by "later wins" both ways (syncSeen): marks
// this device made before v0.338.0 (the old shared KEY_V1) are carried up once,
// so the history already on Scott's phone reaches his laptop.
//
// IF THE SERVER CANNOT BE READ the copy is used alone. That over-shows (a site
// you read elsewhere may look new) — the right way for a marker to be wrong;
// one that under-shows is the same as no marker. It is logged, not hidden.
//
// EVERY localStorage ACCESS IS GUARDED. Private mode, a full quota and blocked
// site data all throw; none of them may take the Locations list down.

import { getCurrentIdentityKey } from './outbox-identity.js';

const KEY_V1 = 'droppin_location_seen_v1';
const KEY_PREFIX = 'droppin_location_seen_v2:';
const MIGRATED_PREFIX = 'droppin_location_seen_v1_up:';
const KEY_RE = /^[A-Za-z0-9_-]{1,64}$/;
const MAX_PER_POST = 500;

function identity() {
  try { return getCurrentIdentityKey() || 'anon'; } catch (_) { return 'anon'; }
}
const storeKey = () => KEY_PREFIX + identity();

function readMap(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const o = JSON.parse(raw);
    return (o && typeof o === 'object' && !Array.isArray(o)) ? o : {};
  } catch (_) { return {}; }
}

function writeMap(key, map) {
  try { localStorage.setItem(key, JSON.stringify(map)); return true; }
  catch (_) { return false; }
}

function keyOf(v) {
  if (v == null || v === '') return null;
  const s = String(v);
  return KEY_RE.test(s) ? s : null;
}

function ms(v) {
  if (typeof v !== 'string' || !v) return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : null;
}

// ── Pure merge (unit-tested) ───────────────────────────────────────────────

/**
 * Merge two { id: iso } maps, later time wins per id. Bad ids and bad times
 * are dropped. Returns { merged, toServer } — toServer holds the ids where
 * `local` is later than (or missing from) `server`, i.e. what to carry up.
 */
export function mergeSeen(local, server) {
  const merged = {};
  const toServer = {};
  const put = (id, iso) => {
    const k = keyOf(id);
    const t = ms(iso);
    if (!k || t == null) return;
    const prev = ms(merged[k]);
    if (prev == null || t > prev) merged[k] = new Date(t).toISOString();
  };
  for (const [id, iso] of Object.entries(server || {})) put(id, iso);
  for (const [id, iso] of Object.entries(local || {})) {
    const k = keyOf(id);
    const t = ms(iso);
    if (!k || t == null) continue;
    const s = ms(server && server[k]);
    if (s == null || t > s) toServer[k] = new Date(t).toISOString();
    put(k, iso);
  }
  return { merged, toServer };
}

// ── Reads (what the list draws from) ───────────────────────────────────────

/** ISO string of the last visit to this location, or null. */
export function lastSeenAt(locationId) {
  const id = keyOf(locationId);
  if (!id) return null;
  const v = readMap(storeKey())[id];
  return typeof v === 'string' && v ? v : null;
}

/** A lookup shaped for countNewReports / the card render. */
export function lastSeenLookup() {
  const all = readMap(storeKey());
  return (row) => {
    const id = keyOf(row && (row.Id != null ? row.Id : row.id));
    return id ? (all[id] || null) : null;
  };
}

// ── Writes ─────────────────────────────────────────────────────────────────

function seenPath(as) {
  return `/api/location-seen${as === 'contact' ? '?as=contact' : ''}`;
}

// Fire-and-forget: the click navigates away at once, so keepalive lets the
// request finish. A failure is fine — the copy here has it, and the next
// syncSeen carries it up.
function postMarks(as, marks) {
  const ids = Object.keys(marks);
  if (!ids.length || identity() === 'anon') return Promise.resolve(false);
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_PER_POST) {
    const part = {};
    for (const id of ids.slice(i, i + MAX_PER_POST)) part[id] = marks[id];
    chunks.push(part);
  }
  return Promise.all(chunks.map((part) =>
    fetch(seenPath(as), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ marks: part }),
      keepalive: true,
      credentials: 'same-origin',
    }).then((r) => r.ok).catch(() => false),
  )).then((oks) => oks.every(Boolean));
}

function record(ids, when, as) {
  const at = when instanceof Date ? when : new Date(when == null ? Date.now() : when);
  const t = at.getTime();
  if (!Number.isFinite(t)) return false;
  const iso = new Date(t).toISOString();
  const key = storeKey();
  const all = readMap(key);
  const sent = {};
  for (const raw of ids) {
    const id = keyOf(raw);
    if (!id) continue;
    all[id] = iso;
    sent[id] = iso;
  }
  if (!Object.keys(sent).length) return false;
  const ok = writeMap(key, all);
  if (as) postMarks(as, sent);
  return ok;
}

/**
 * Record that the reader is opening this location NOW.
 *
 * Called on the click that navigates in, never on render: a marker cleared by
 * the list drawing itself is a marker nobody ever sees. `as` ('contact' |
 * 'office') also sends it to the server; without it only this browser keeps it.
 */
export function markSeen(locationId, when, as) {
  return record([locationId], when, as);
}

/** The "mark all as seen" affordance. */
export function markAllSeen(rows, when, as) {
  const list = Array.isArray(rows) ? rows : [];
  return record(list.map((row) => row && (row.Id != null ? row.Id : row.id)), when, as);
}

// ── Sync ───────────────────────────────────────────────────────────────────

/**
 * Bring this browser's copy and the server's marks together, later wins.
 * Resolves { ok: true } or { ok: false, error } — never throws; a failed read
 * leaves the copy as it was (over-shows, see the top of the file).
 */
export async function syncSeen(as) {
  const who = identity();
  if (who === 'anon') return { ok: false, error: 'no-identity' };
  const key = KEY_PREFIX + who;
  let server;
  try {
    const r = await fetch(seenPath(as), {
      headers: { 'X-DropPin-Fresh': '1' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!r.ok) throw new Error(`GET location-seen → ${r.status}`);
    const j = await r.json();
    server = (j && j.marks && typeof j.marks === 'object') ? j.marks : {};
  } catch (e) {
    console.warn('[DropPin] location-seen: could not read your marks; using this device\'s only.', e);
    return { ok: false, error: String((e && e.message) || e) };
  }

  let local = readMap(key);
  // Marks made on this device before v0.338.0 — carried up once per person.
  let migratedNow = false;
  try {
    if (!localStorage.getItem(MIGRATED_PREFIX + who)) {
      local = mergeSeen(local, readMap(KEY_V1)).merged;
      migratedNow = true;
    }
  } catch (_) { /* storage blocked: nothing to carry */ }

  const { merged, toServer } = mergeSeen(local, server);
  writeMap(key, merged);
  const sent = Object.keys(toServer).length ? await postMarks(as, toServer) : true;
  if (migratedNow && sent) {
    try { localStorage.setItem(MIGRATED_PREFIX + who, new Date().toISOString()); } catch (_) {}
  }
  return { ok: true, uploaded: Object.keys(toServer).length, sent };
}
